<script setup lang="ts">
import BackButton from '@/core/elements/BackButton.vue';
import IconButton from '@/core/elements/IconButton.vue';
import { EIcon } from 'common/enum/misc.enum';

const emit = defineEmits<{
  'click:hide': [];
  'click:back': [];
}>();

withDefaults(
  defineProps<{
    title?: string;
    showCloseButton?: boolean;
    showBackButton?: boolean;
    size?: 'md' | 'lg';
    theme?: 'default' | 'checkmark' | 'note';
    bordered?: boolean;
    scrollable?: boolean;
    isWideLayout?: boolean;
  }>(),
  {
    title: undefined,
    showCloseButton: true,
    showBackButton: false,
    size: 'lg',
    theme: 'default',
    bordered: false,
    scrollable: false,
    isWideLayout: false,
  },
);
</script>

<template>
  <header
    v-if="title || showCloseButton || showBackButton || $slots.title"
    class="flex items-center justify-between gap-md px-content max-md:h-18 max-md:min-h-18 max-md:px-lg max-md:py-md"
    :class="[
      size === 'md' && 'p-md',
      size === 'lg' && 'min-h-header px-content py-lg',
      bordered && 'border-b',
      scrollable && 'overflow-y-hidden max-md:overflow-x-auto',
      scrollable && isWideLayout && '[@container(max-width:959px)]:overflow-x-auto',
    ]"
  >
    <div
      :class="[
        'grid min-w-0 flex-1 items-center gap-xs',
        showBackButton ? 'grid-cols-[auto_1fr]' : 'grid-cols-[1fr]',
        scrollable && 'overflow-y-hidden max-md:shrink-0',
        scrollable && isWideLayout && '[@container(max-width:959px)]:shrink-0',
      ]"
    >
      <BackButton v-if="showBackButton" @click.stop="emit('click:back')" />
      <template v-if="$slots.title">
        <slot name="title"></slot>
      </template>

      <h1 v-else :class="[size === 'md' && 'text-subtitle', size === 'lg' && 'text-title', 'font-medium truncate']">
        {{ title }}
      </h1>
    </div>
    <div class="flex shrink-0 items-center gap-md" :class="{ 'py-sm': size === 'lg' }">
      <slot name="actions"></slot>
      <IconButton
        v-if="showCloseButton && theme !== 'checkmark'"
        type="button"
        size="button"
        :icon-name="EIcon.X"
        :aria-label="$t('general.close')"
        :theme="theme === 'note' ? 'note' : 'text'"
        class="max-md:bg-surface-secondary"
        @click="emit('click:hide')"
      />
      <IconButton
        v-if="showCloseButton && theme === 'checkmark'"
        type="button"
        size="button"
        :icon-name="EIcon.CHECKMARK"
        :aria-label="$t('general.close')"
        theme="primary-filled"
        @click="emit('click:hide')"
      />
    </div>
  </header>
</template>
